import type { StudioChoice, ThemeColorToken } from '@base/shared';
import { RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageField, readPlatformImage } from '../components/ImageField.tsx';
import { Field, Input, Panel } from '../components/ui.tsx';
import { CheckList } from './CheckList.tsx';
import { reviewOf, withColor, type PaletteColorKey } from './state.ts';

/** O nome e o logo do sistema, mexidos no estúdio e gravados com o estilo (ADR 0133, parte 4). */
export interface StudioBrand {
  name: string | null;
  logo: string | null;
}

/** A marca: o logo (que pode virar a paleta) e o nome que aparece no menu. */
export function BrandPanel({
  brand,
  onChange,
  defaultLogo,
  placeholder,
  readOnly,
}: {
  brand: StudioBrand;
  onChange: (b: StudioBrand) => void;
  defaultLogo: string;
  placeholder: string;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string>();
  const pick = async (file: File | undefined) => {
    setError(undefined);
    if (!file) return;
    const read = await readPlatformImage(file);
    if ('error' in read) setError(t(`platform.${read.error}`));
    else onChange({ ...brand, logo: read.url });
  };
  return (
    <Panel title={t('style.brand')}>
      <ImageField
        label={t('platform.logo')}
        hint={t('style.brandLogoHint')}
        fallback={defaultLogo}
        value={brand.logo}
        readOnly={readOnly}
        onPick={(f) => void pick(f)}
        onReset={() => onChange({ ...brand, logo: null })}
      />
      {error && <p className="text-sm text-danger">{error}</p>}
      <Field label={t('style.brandName')} hint={t('style.brandNameHint')}>
        <Input
          value={brand.name ?? ''}
          maxLength={40}
          placeholder={placeholder}
          disabled={readOnly}
          onChange={(e) => onChange({ ...brand, name: e.target.value || null })}
        />
      </Field>
    </Panel>
  );
}

/** As cores que se ajustam, por grupo, com a cor do tema que cada uma muda. */
const COLOR_GROUPS: { id: string; colors: [PaletteColorKey, ThemeColorToken][] }[] = [
  {
    id: 'screen',
    colors: [
      ['paper', 'paper'],
      ['sheet', 'sheet'],
      ['ink', 'ink'],
      ['muted', 'muted'],
      ['rule', 'rule'],
    ],
  },
  {
    id: 'bars',
    colors: [
      ['sidebar', 'sidebar'],
      ['sidebarText', 'sidebar-text'],
    ],
  },
  {
    id: 'status',
    colors: [
      ['ok', 'ok'],
      ['gold', 'gold'],
      ['danger', 'danger'],
      ['info', 'info'],
    ],
  },
];

/**
 * Ajustar as cores da paleta, uma a uma (ADR 0133, parte 4): a tela, o menu e as barras e as
 * cores de situação. Cada cor mostra a que vale agora; a trocada volta à da paleta. Embaixo, a
 * conferência de contraste já com os ajustes.
 */
export function ColorAdjust({
  value,
  onChange,
  readOnly,
}: {
  value: StudioChoice;
  onChange: (next: StudioChoice) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const { theme } = reviewOf(value, '');
  const now = theme.colors.light;
  const set = value.palette.colors ?? {};
  const changed = Object.keys(set).length;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="ui-head text-sm font-semibold">{t('style.colorAdjust')}</h2>
        {changed > 0 && !readOnly && (
          <button
            type="button"
            className="text-xs text-brand-text underline-offset-2 hover:underline"
            onClick={() =>
              onChange(
                COLOR_GROUPS.flatMap((g) => g.colors).reduce(
                  (s, [k]) => withColor(s, k, undefined),
                  value,
                ),
              )
            }
          >
            {t('style.colorResetAll')}
          </button>
        )}
      </div>
      {COLOR_GROUPS.map((g) => {
        const count = g.colors.filter(([k]) => set[k]).length;
        return (
          <details
            key={g.id}
            className="ui-panel rounded-lg bg-sheet [&_summary::-webkit-details-marker]:hidden"
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-medium">
              <span className="flex items-center gap-2">
                {t(`style.colorGroups.${g.id}`)}
                <span aria-hidden className="flex">
                  {g.colors.map(([k, token]) => (
                    <span
                      key={k}
                      className="-ml-1 size-4 rounded-full border border-rule first:ml-0"
                      style={{ backgroundColor: now[token] }}
                    />
                  ))}
                </span>
              </span>
              <span className="text-xs font-normal text-muted">
                {count ? t('style.colorChanged', { count }) : t('style.colorFromPalette')}
              </span>
            </summary>
            <ul className="flex flex-col gap-2 border-t border-rule px-4 py-3">
              {g.colors.map(([k, token]) => {
                const label = t(`style.tokens.${token}`);
                const color = (set[k] ?? now[token] ?? '#000000').toLowerCase();
                return (
                  <li key={k} className="flex items-center gap-3 text-sm">
                    <input
                      type="color"
                      aria-label={label}
                      disabled={readOnly}
                      value={color}
                      onChange={(e) => onChange(withColor(value, k, e.target.value))}
                      className="h-8 w-10 shrink-0 cursor-pointer rounded border border-control-border bg-sheet"
                    />
                    <span className="min-w-0 flex-1 first-letter:uppercase">{label}</span>
                    <span className="font-mono text-xs text-muted">{color.toUpperCase()}</span>
                    {set[k] && !readOnly ? (
                      <button
                        type="button"
                        onClick={() => onChange(withColor(value, k, undefined))}
                        title={t('style.colorReset')}
                        aria-label={`${t('style.colorReset')}: ${label}`}
                        className="rounded-md p-1 text-muted hover:text-ink"
                      >
                        <RotateCcw size={14} aria-hidden />
                      </button>
                    ) : (
                      <span className="w-[22px]" />
                    )}
                  </li>
                );
              })}
            </ul>
          </details>
        );
      })}
      <div className="ui-panel rounded-lg bg-sheet p-4">
        <CheckList value={value} onChange={onChange} readOnly={readOnly} />
      </div>
    </div>
  );
}
